import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  FileText,
  Sparkles,
  CheckCircle2,
  Check,
  ArrowRight,
  ChevronRight,
  Send,
  Building2,
  PenTool,
  Search,
  BookOpen,
  Target,
  Users,
  Award,
  TrendingUp,
} from "lucide-react";

export const Route = createFileRoute("/services_/copywriting")({
  head: () => ({
    meta: [
      {
        title:
          "Creative Copywriting & Brand Writing Services — KMWAdvertising LLP",
      },
      {
        name: "description",
        content:
          "Creative Copywriting Services by KMWAdvertising LLP. Brand messaging, website copywriting, ad scripts, SEO content, and performance marketing copy.",
      },
      {
        property: "og:title",
        content: "Creative Copywriting Services — KMWAdvertising LLP",
      },
      {
        property: "og:description",
        content:
          "Words that inspire action and build stronger brands. Discover our strategic brand, marketing, and SEO copywriting services.",
      },
    ],
  }),
  component: CopywritingServicePage,
});

export function CopywritingServicePage() {
  const [formSubmitted, setFormSubmitted] = useState(false);

  const brandCopywritingList = [
    "Brand Messaging",
    "Brand Story Development",
    "Vision & Mission Statements",
    "Brand Positioning Statements",
    "Taglines & Slogans",
    "Tone of Voice Guidelines",
    "Corporate Profiles",
    "Company Presentations",
  ];

  const marketingCopywritingList = [
    "Website Copywriting",
    "Landing Pages",
    "SEO Content",
    "Google Ads Copy",
    "Meta Ads Copy",
    "LinkedIn Campaign Copy",
    "Email Marketing Content",
    "Product Descriptions",
    "Sales Brochures",
    "Print Advertisements",
    "Outdoor Advertising Copy",
    "Radio Scripts",
    "Television Commercial Scripts",
    "Video Scripts",
    "Social Media Captions",
    "Press Releases",
    "Case Studies",
    "Whitepapers",
    "Blog Articles",
  ];

  const seoCopywritingList = [
    "Keyword Research",
    "Search Intent Analysis",
    "Optimised Headings",
    "Internal Linking",
    "Semantic Keyword Integration",
    "EEAT Optimisation",
    "Featured Snippet Optimisation",
    "AI Search Optimisation",
  ];

  return (
    <div className="min-h-screen bg-[#07070d] text-slate-100 selection:bg-pink-500 selection:text-white font-sans relative overflow-x-hidden">
      <SiteNav />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-gradient-to-tr from-pink-600/20 via-purple-600/15 to-rose-500/10 blur-[130px] rounded-full pointer-events-none" />
        <div className="absolute top-10 right-10 w-96 h-96 bg-pink-500/10 blur-[120px] rounded-full pointer-events-none" />

        <div className="container-x relative z-10">
          <div className="flex items-center gap-2 text-xs font-mono text-slate-400 mb-6">
            <Link to="/" className="hover:text-white transition">
              Home
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <Link to="/services" className="hover:text-white transition">
              Services
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <Link to="/services/branding-creatives" className="hover:text-white transition">
              Branding & Creatives
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <span className="text-pink-400">Copywriting</span>
          </div>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase tracking-wider mb-6">
              <PenTool className="h-3.5 w-3.5" />
              Creative Copywriting Services
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.12] mb-6">
              Creative Copywriting
              <span className="block bg-gradient-to-r from-pink-400 via-rose-300 to-purple-300 bg-clip-text text-transparent mt-1">
                Words That Inspire Action and Build Stronger Brands
              </span>
            </h1>

            <div className="space-y-4 text-slate-300 text-base sm:text-lg leading-relaxed mb-8 max-w-3xl">
              <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/10 space-y-2 mb-6">
                <p className="text-pink-300 font-bold text-lg">Exceptional design captures attention.</p>
                <p className="text-white font-bold text-lg">Exceptional copy inspires action.</p>
              </div>

              <p>
                Every successful brand relies on clear, persuasive and emotionally engaging communication. Whether you're launching a product, running a campaign or redesigning your website, compelling copy helps translate your brand's value into messages that resonate with your audience.
              </p>
              <p className="text-sm sm:text-base text-pink-300/90 font-medium">
                At KMWAdvertising LLP, our copywriters combine marketing psychology, storytelling, SEO and brand strategy to craft content that informs, persuades and converts.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <a
                href="#copy-form"
                className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-600 to-purple-600 text-white font-bold text-sm shadow-[0_0_30px_rgba(236,72,153,0.35)] hover:shadow-[0_0_40px_rgba(236,72,153,0.5)] hover:scale-[1.02] transition-all"
              >
                <span>Request Copywriting Brief</span>
                <ArrowRight className="h-4 w-4" />
              </a>

              <a
                href="#brand-copywriting"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white/5 border border-white/10 text-white font-semibold text-sm hover:bg-white/10 hover:border-white/20 transition-all"
              >
                <span>Explore Copywriting Pillars</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Copywriting */}
      <section id="brand-copywriting" className="py-20 md:py-28 bg-[#0a0a14] border-y border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase">
              <Target className="h-3.5 w-3.5" />
              Voice & Identity
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Brand Copywriting
            </h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              We help businesses define a distinctive voice that reflects their personality and values. Our brand writing services include:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {brandCopywritingList.map((item) => (
              <div
                key={item}
                className="p-4 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-pink-500/30 transition flex items-center gap-3"
              >
                <Check className="h-4 w-4 text-pink-400 shrink-0" />
                <span className="text-xs sm:text-sm font-semibold text-white">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Marketing Copywriting */}
      <section className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-mono font-bold uppercase">
              <BookOpen className="h-3.5 w-3.5" />
              Omnichannel Performance Copy
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Marketing Copywriting
            </h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Our marketing content is designed to improve engagement and campaign performance across every channel. Services include:
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-8">
            {marketingCopywritingList.map((item) => (
              <div
                key={item}
                className="p-3.5 rounded-xl bg-white/[0.02] border border-white/10 hover:border-purple-500/30 transition text-center text-xs font-semibold text-white"
              >
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Copywriting */}
      <section className="py-20 md:py-28 bg-[#0a0a14] border-t border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs font-mono font-bold uppercase">
              <Search className="h-3.5 w-3.5" />
              Search Engine Optimisation
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              SEO Copywriting
            </h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Content is created not only for readers but also for search engines. Our SEO copywriting includes:
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
            {seoCopywritingList.map((item) => (
              <div
                key={item}
                className="p-4 rounded-2xl bg-white/5 border border-white/10 text-center text-xs font-bold text-slate-200 hover:border-rose-500/40 transition"
              >
                {item}
              </div>
            ))}
          </div>

          <div className="p-8 rounded-3xl bg-gradient-to-r from-pink-950/30 via-rose-950/20 to-purple-950/30 border border-white/15 mb-16">
            <p className="text-sm sm:text-base text-slate-200 leading-relaxed font-medium">
              Every piece of content is developed to improve visibility, readability and conversions while maintaining a natural, human-first writing style.
            </p>
          </div>

          {/* Why Choose Our Creative Team? */}
          <div className="p-8 sm:p-12 rounded-3xl bg-gradient-to-br from-pink-950/50 via-[#0e0e1c] to-purple-950/40 border border-pink-500/30 space-y-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-pink-500/20 border border-pink-500/40 text-pink-300 text-xs font-mono font-bold uppercase">
              <Award className="h-3.5 w-3.5" />
              Integrated Excellence
            </div>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white">
              Why Choose Our Creative Team?
            </h3>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              At KMWAdvertising LLP, creativity is backed by strategy, media expertise and performance insights. Unlike standalone creative studios, our designers, writers, photographers, filmmakers and digital marketers collaborate from the beginning of every project. This integrated approach ensures that every logo, video, photograph, animation and piece of copy is not only visually compelling but also aligned with your business objectives, audience behaviour and marketing channels, delivering creative work that drives measurable brand growth rather than simply looking impressive.
            </p>
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="copy-form" className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-tr from-pink-950/40 via-[#0d0d1a] to-purple-950/30 border border-white/20 p-8 sm:p-12 shadow-2xl relative overflow-hidden">
            <div className="glow-orb -top-20 -right-20 h-64 w-64 bg-pink-500 opacity-20" />

            <div className="relative z-10 max-w-2xl mx-auto text-center space-y-4 mb-10">
              <span className="text-xs font-mono font-bold uppercase tracking-wider text-pink-400">
                Copywriting Project Brief
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
                Transform Your Message with High-Impact Copy
              </h2>
              <p className="text-slate-300 text-sm">
                Discuss your brand voice, website copy, ad campaign scripts, or SEO content strategy with our senior copywriters.
              </p>
            </div>

            {formSubmitted ? (
              <div className="p-8 rounded-2xl bg-pink-500/10 border border-pink-500/30 text-center space-y-4">
                <CheckCircle2 className="h-12 w-12 text-pink-400 mx-auto" />
                <h3 className="text-xl font-bold text-white">Copywriting Brief Received!</h3>
                <p className="text-slate-300 text-sm">
                  Our chief strategist will get back to you within 24 hours to schedule your content consultation.
                </p>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setFormSubmitted(true);
                }}
                className="space-y-6 relative z-10"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Your Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Varun Sharma"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Company / Brand Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Zenith Tech Solutions"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Email Address *</label>
                    <input
                      type="email"
                      required
                      placeholder="varun@company.com"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      required
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-300 mb-2">Copywriting Scope</label>
                  <select className="w-full px-4 py-3 rounded-xl bg-[#0e0e1a] border border-white/15 text-white text-sm focus:outline-none focus:border-pink-500 transition">
                    <option value="brand">Brand Messaging & Tone of Voice Guidelines</option>
                    <option value="marketing">Website & Performance Marketing Copy</option>
                    <option value="seo">SEO Copywriting & Search Intent Content</option>
                    <option value="ad-scripts">Ad Commercials & Video Scriptwriting</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-300 mb-2">Project Brief / Objectives</label>
                  <textarea
                    rows={4}
                    placeholder="Describe your brand's core offer, audience profile, key deadlines..."
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-600 to-purple-600 text-white font-bold text-sm shadow-xl hover:shadow-[0_0_40px_rgba(236,72,153,0.5)] transition-all flex items-center justify-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  <span>Submit Copywriting Brief</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
